// client/src/components/bank/BankDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDuration, formatCurrencyWithSymbol } from '../utils/formatUtils';
import { Menu, Play, Star, History, Mail, Filter, Receipt } from 'lucide-react';

// One row of a split stat box: label + count, linking to the relevant page when count > 0
const StatRow = ({ label, value, to, state, isLast }) => {
  const isLink = value > 0 && !!to;
  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 2px',
    borderBottom: isLast ? 'none' : '1px solid #e0e0e0',
    textDecoration: 'none',
    color: 'inherit'
  };
  const inner = (
    <>
      <span style={{
        fontSize: '0.9rem',
        color: isLink ? '#1a73e8' : '#666',
        textDecoration: isLink ? 'underline' : 'none'
      }}>
        {label}
      </span>
      <span style={{ fontSize: '22px', fontWeight: '700', color: '#1a73e8' }}>{value}</span>
    </>
  );
  if (isLink) {
    return (
      <Link to={to} state={state} style={rowStyle}>
        {inner}
      </Link>
    );
  }
  return <div style={rowStyle}>{inner}</div>;
};

const BankDashboard = () => {
  const { t } = useTranslation();
  const [allRequests, setAllRequests] = useState([]);
  const [stats, setStats] = useState({
    openRequests: 0,
    activeBids: 0,
    activeBidsDeposits: 0,
    activeBidsHotMoney: 0,
    totalBiddingValue: 0,
    unbiddedRequests: 0,
    unbiddedDeposits: 0,
    unbiddedHotMoney: 0,
    currencyBreakdown: {}
  });
  const [vatCreditStats, setVatCreditStats] = useState({ unbidded: 0, activeBids: 0 });

  const [newDepositOpportunities, setNewDepositOpportunities] = useState([]);
  const [newHotMoneyOpportunities, setNewHotMoneyOpportunities] = useState([]);
  const [newVatCreditOpportunities, setNewVatCreditOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAuthenticated, token, initialized, user } = useAuth();
  // Non-referral banks (and all their users) only deal with deposits:
  // hide the Hot Money and VAT Credit rows in the dashboard stat boxes.
  const isReferral = user?.isReferral === true;
  
  // Function to calculate stats (no currency filter - shows ALL)
  const calculateStats = (requests, bids) => {
    // Filter unbidded requests (considering only active bids, not cancelled ones)
    const validBids = bids.filter(bid => bid.request !== null);
    const unbiddedRequests = requests.filter(request => {
      const hasActiveBidForRequest = validBids.some(bid => {
        return bid.request?.requestId === request.requestId && bid.status !== 'cancelled';
      });
      return !hasActiveBidForRequest;
    });
    
    // Calculate active bids (ALL), split by request type
    const activeBidsList = validBids.filter(bid => bid.status === 'pending');
    const activeBids = activeBidsList.length;
    const activeBidsDeposits = activeBidsList.filter(bid => bid.request?.requestType !== 'hot_money').length;
    const activeBidsHotMoney = activeBidsList.filter(bid => bid.request?.requestType === 'hot_money').length;

    // Split unbidded requests by type
    const unbiddedDeposits = unbiddedRequests.filter(request => request.requestType !== 'hot_money').length;
    const unbiddedHotMoney = unbiddedRequests.filter(request => request.requestType === 'hot_money').length;

    // Calculate total bidding value (ALL)
    const totalBiddingValue = requests.reduce(
      (sum, request) => sum + request.amount, 
      0
    );
    
    // Calculate currency breakdown for all requests
    const currencyBreakdown = {};
    requests.forEach(request => {
      const curr = request.currency || 'EUR';
      if (!currencyBreakdown[curr]) {
        currencyBreakdown[curr] = {
          totalValue: 0,
          requestCount: 0
        };
      }
      currencyBreakdown[curr].totalValue += request.amount;
      currencyBreakdown[curr].requestCount += 1;
    });
    
    return {
      openRequests: requests.length,
      activeBids,
      activeBidsDeposits,
      activeBidsHotMoney,
      totalBiddingValue,
      unbiddedRequests: unbiddedRequests.length,
      unbiddedDeposits,
      unbiddedHotMoney,
      currencyBreakdown,
      filteredUnbiddedRequests: unbiddedRequests
    };
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Make sure we don't try to fetch before authentication is initialized
      if (!initialized) {
        return;
      }
      
      // Reset states when starting a new fetch
      setLoading(true);
      setError('');
      
      try {
        // Make sure the token is set in axios headers
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        // Fetch active requests that are open for bidding
        const requestsRes = await axios.get('/api/requests?status=bidding');
        
        // Fetch bank bids
        const bidsRes = await axios.get('/api/bids/bank');
        
        // Store all requests for currency filtering
        setAllRequests(requestsRes.data.requests);
        
        // Calculate initial stats
        const calculatedStats = calculateStats(
          requestsRes.data.requests, 
          bidsRes.data.bids
        );
        
        setStats(calculatedStats);
        
        // Set new opportunities (unbidded requests), split by type - limit to 3 each
        setNewDepositOpportunities(
          calculatedStats.filteredUnbiddedRequests.filter(request => request.requestType !== 'hot_money').slice(0, 3)
        );
        setNewHotMoneyOpportunities(
          calculatedStats.filteredUnbiddedRequests.filter(request => request.requestType === 'hot_money').slice(0, 3)
        );

        // Filter out bids with null requests for activity
        const validBids = bidsRes.data.bids.filter(bid => bid.request !== null);

        // Fetch Crediti IVA open requests + own bids, to notify unbidded ones (same pattern as above)
        try {
          const vatCreditRequestsRes = await axios.get('/api/vat-credit-requests');
          const vatCreditBidsRes = await axios.get('/api/vat-credit-bids/bank');

          const vatCreditValidBids = (vatCreditBidsRes.data.bids || []).filter(bid => bid.request !== null);
          const vatCreditUnbidded = (vatCreditRequestsRes.data.requests || []).filter(request => {
            const hasActiveBidForRequest = vatCreditValidBids.some(bid =>
              bid.request?.requestId === request.requestId && bid.status !== 'cancelled'
            );
            return !hasActiveBidForRequest;
          });

          setNewVatCreditOpportunities(vatCreditUnbidded.slice(0, 3));

          const vatCreditActiveBids = vatCreditValidBids.filter(bid => bid.status === 'pending').length;
          setVatCreditStats({ unbidded: vatCreditUnbidded.length, activeBids: vatCreditActiveBids });
        } catch (vatCreditErr) {
          console.error('VAT credit dashboard data fetch error:');
          setNewVatCreditOpportunities([]);
          setVatCreditStats({ unbidded: 0, activeBids: 0 });
        }

        setLoading(false);
      } catch (err) {
        console.error('Dashboard data fetch error:');
        setError(t('common.errors.fetchDashboard'));
        setLoading(false);
      }
    };
    
    // Only fetch data if authenticated and initialized
    if (isAuthenticated && initialized) {
      fetchDashboardData();
    }
  }, [isAuthenticated, token, initialized, t]);
  
  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <div>{t('common.loadingDashboard')}</div>
      </div>
    );
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="bank-dashboard company-dashboard">
      {/* Enhanced Stats grid with card styling */}
      <div className="stats-container" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className="card stat-card" style={{ margin: 0, transition: 'all 0.3s ease' }}>
          <div className="card-title" style={{ margin: '-20px -20px 15px -20px' }}>
            <Menu className="card-title-icon" size={20} />
            <span>{t('dashboard.openRequests')}</span>
          </div>
          <div>
            <StatRow label={t('requests.allocation')} value={stats.unbiddedDeposits} to="/bank/open-requests/allocation" isLast={!isReferral} />
            {isReferral && (
              <>
                <StatRow label={t('requests.hotMoney')} value={stats.unbiddedHotMoney} to="/bank/open-requests/hot-money" />
                <StatRow label={t('requests.vatCredit')} value={vatCreditStats.unbidded} to="/bank/vat-credit/open-requests" isLast />
              </>
            )}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#666', textAlign: 'center', marginTop: '10px' }}>
            {t('dashboard.newOpportunities')}
          </div>
        </div>

        <div className="card stat-card" style={{ margin: 0, transition: 'all 0.3s ease' }}>
          <div className="card-title" style={{ margin: '-20px -20px 15px -20px' }}>
            <Play className="card-title-icon" size={20} />
            <span>{t('dashboard.activeBids')}</span>
          </div>
          <div>
            <StatRow label={t('requests.allocation')} value={stats.activeBidsDeposits} to="/bank/past-bids/allocation" state={{ activeTab: 'pending' }} isLast={!isReferral} />
            {isReferral && (
              <>
                <StatRow label={t('requests.hotMoney')} value={stats.activeBidsHotMoney} to="/bank/past-bids/hot-money" state={{ activeTab: 'pending' }} />
                <StatRow label={t('requests.vatCredit')} value={vatCreditStats.activeBids} to="/bank/vat-credit/past-bids" state={{ activeTab: 'pending' }} isLast />
              </>
            )}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#666', textAlign: 'center', marginTop: '10px' }}>
            {t('dashboard.pendingApproval')}
          </div>
        </div>

      </div>
      
      {/* New deposit opportunities card with dividers between items */}
      {newDepositOpportunities.length > 0 && (
        <div className="card">
          <div className="card-title">
            <Star className="card-title-icon" size={20} />
            <span>
              {t('dashboard.newDepositOpportunitiesTitle')}
            </span>
          </div>
          <div style={{ padding: '0' }}>
            {newDepositOpportunities.map((request, index) => (
              <React.Fragment key={request.id}>
                <div className="opportunity-item" style={{ padding: '15px 0' }}>
                  <p style={{ marginBottom: '12px', fontSize: '16px' }}>
                    {t('requests.newLiquidityPosted', {
                      companyName: request.company?.name,
                      amount: formatCurrencyWithSymbol(request.amount, request.currency || 'EUR'),
                      duration: formatDuration(request.duration, t)
                    })}
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Link
                      to={`/bank/submit-bid/${request.id}`}
                      className="btn btn-primary"
                    >
                      {t('bids.submitBid')}
                    </Link>
                    <Link
                      to={`/bank/open-requests`}
                      className="btn btn-secondary"
                    >
                      {t('bids.viewAllRequests')}
                    </Link>
                  </div>
                </div>
                {/* Add horizontal divider line between items, but not after the last item */}
                {index < newDepositOpportunities.length - 1 && (
                  <hr style={{
                    margin: '0',
                    border: 'none',
                    height: '1px',
                    backgroundColor: '#e0e0e0'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* New hot money opportunities card, same pattern as the deposit one above */}
      {isReferral && newHotMoneyOpportunities.length > 0 && (
        <div className="card">
          <div className="card-title">
            <Star className="card-title-icon" size={20} />
            <span>
              {t('dashboard.newHotMoneyOpportunitiesTitle')}
            </span>
          </div>
          <div style={{ padding: '0' }}>
            {newHotMoneyOpportunities.map((request, index) => (
              <React.Fragment key={request.id}>
                <div className="opportunity-item" style={{ padding: '15px 0' }}>
                  <p style={{ marginBottom: '12px', fontSize: '16px' }}>
                    {t('requests.newHotMoneyPosted', {
                      companyName: request.company?.name,
                      amount: formatCurrencyWithSymbol(request.amount, request.currency || 'EUR'),
                      duration: formatDuration(request.duration, t)
                    })}
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Link
                      to={`/bank/submit-bid/${request.id}`}
                      className="btn btn-primary"
                    >
                      {t('bids.submitBid')}
                    </Link>
                    <Link
                      to={`/bank/open-requests`}
                      className="btn btn-secondary"
                    >
                      {t('bids.viewAllRequests')}
                    </Link>
                  </div>
                </div>
                {index < newHotMoneyOpportunities.length - 1 && (
                  <hr style={{
                    margin: '0',
                    border: 'none',
                    height: '1px',
                    backgroundColor: '#e0e0e0'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* New VAT credit opportunities card, same pattern as the liquidity one above */}
      {newVatCreditOpportunities.length > 0 && (
        <div className="card">
          <div className="card-title">
            <Receipt className="card-title-icon" size={20} />
            <span>
              {t('dashboard.newVatCreditOpportunitiesTitle')}
            </span>
          </div>
          <div style={{ padding: '0' }}>
            {newVatCreditOpportunities.map((request, index) => (
              <React.Fragment key={request.id}>
                <div className="opportunity-item" style={{ padding: '15px 0' }}>
                  <p style={{ marginBottom: '12px', fontSize: '16px' }}>
                    {t('requests.newVatCreditPosted', {
                      companyName: request.company?.name,
                      amount: formatCurrencyWithSymbol(request.amount, 'EUR')
                    })}
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Link
                      to={`/bank/vat-credit/submit-bid/${request.id}`}
                      className="btn btn-primary"
                    >
                      {t('bids.submitBid')}
                    </Link>
                    <Link
                      to={`/bank/vat-credit/open-requests`}
                      className="btn btn-secondary"
                    >
                      {t('bids.viewAllRequests')}
                    </Link>
                  </div>
                </div>
                {index < newVatCreditOpportunities.length - 1 && (
                  <hr style={{
                    margin: '0',
                    border: 'none',
                    height: '1px',
                    backgroundColor: '#e0e0e0'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}


      {/* Support Contact Section */}
      <div className="card" style={{ textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
          <Mail size={16} style={{ color: '#6c757d' }} />
          <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>
            {t('dashboard.needHelp', { defaultValue: 'Hai bisogno di aiuto?' })}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
          {t('dashboard.contactSupport', { defaultValue: 'Contatta il nostro supporto:' })}{' '}
          <a href="mailto:support@euracle.it" style={{ color: '#1a73e8', textDecoration: 'none' }}>
            support@euracle.it
          </a>
        </p>
      </div>
    </div>
  );
};

export default BankDashboard;